import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import { useSesi } from '../../../app/auth'
import { useAbaikan, useAmbil, useAntrean, useBalas } from '../../../domains/conversation/api'
import { sejak } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, BilahTabel, Chip, Gagal, KepalaHalaman, Kosong, Memuat, Panel, Tabel, Tombol,
} from '../../../shared/ui'

export default function PerluDijawab() {
  const { t, locale } = useBahasa()
  const { sesi } = useSesi()
  // `?cari=` DIBACA SEKALI, sebagai nilai awal — bukan diikat dua arah ke URL. Layar
  // detail prospek menautkan ke sini dengan nama pengirimnya supaya "Balas dari sini"
  // mendarat pada percakapan yang benar, bukan di ujung antrean lima puluh baris. Yang
  // mengetik sesudahnya tetap mengubah kotaknya, dan URL-nya tidak ikut berubah.
  const [sp] = useSearchParams()
  const [cari, setCari] = useState(() => sp.get('cari') || '')
  const [diambil, setDiambil] = useState('')
  // Baris yang penyusun balasannya sedang terbuka. Satu saja: dua penyusun terbuka
  // bersamaan mengundang teks dikirim ke baris yang salah.
  const [menulis, setMenulis] = useState(null)
  const [teks, setTeks] = useState('')

  const ambil = useAmbil()
  const balas = useBalas()
  const abaikan = useAbaikan()

  // Penyaringan dan pencarian dikerjakan SERVER, bukan klien. Antrean ini yang
  // paling cepat panjang dari semua tabel di aplikasi — tiap DM dan tiap komentar
  // menambah satu baris — jadi menariknya seluruhnya lalu menyaring di browser
  // akan patah lebih dulu daripada tabel mana pun.
  const q = useAntrean({
    status: 'pending',
    cari: cari.trim() || undefined,
    diambil: diambil || undefined,
    limit: 50,
  })

  const items = q.data?.items ?? []

  return (
    <>
      <KepalaHalaman judul={t('masuk.judul')} keterangan={t('masuk.keterangan')} />

      <Panel>
        <BilahTabel
          cari={{ nilai: cari, set: setCari }}
          saring={{
            nilai: diambil,
            set: setDiambil,
            opsi: [
              { nilai: '', label: t('masuk.semuaKeadaan') },
              { nilai: 'belum', label: t('masuk.hanyaBelum') },
              { nilai: 'saya', label: t('masuk.hanyaSaya') },
              { nilai: 'orang_lain', label: t('masuk.hanyaOrangLain') },
            ],
          }}
          ringkas={
            q.data
              ? t('umum.dari', {
                  mulai: items.length ? q.data.offset + 1 : 0,
                  akhir: q.data.offset + items.length,
                  total: q.data.total,
                })
              : null
          }
        />

        {q.isLoading && <Memuat tinggi={4} />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

        {/* DI ATAS TABEL, bukan di dalam barisnya. Baris yang berhasil diabaikan HILANG
            dari daftar, jadi pesan yang menempel padanya ikut hilang bersamanya — dan
            yang GAGAL tetap di tempatnya tanpa satu pun keterangan kenapa tombolnya
            tidak melakukan apa-apa. */}
        {abaikan.isError && (
          <div className="px-3.5 pt-3">
            <Bendera nada="bad" role="alert">
              {t('masuk.abaikanGagal', { sebab: abaikan.error?.message || '—' })}
            </Bendera>
          </div>
        )}

        {q.data && items.length === 0 && (
          <Kosong
            judul={t('masuk.kosong')}
            keterangan={
              cari || diambil
                ? undefined
                : 'Yang bot bisa jawab sendiri tidak muncul di sini — hanya yang ia serahkan ke tim.'
            }
          />
        )}

        {items.length > 0 && (
          <Tabel
            kepala={[
              t('masuk.datang'), t('masuk.dari'), t('masuk.pesan'),
              t('masuk.jenis'), t('masuk.bot'), t('masuk.keadaan'), '',
            ]}
          >
            {items.flatMap((e) => {
              const bebas = !e.web_user_id && !e.claimer_username
              // Kepemilikan dinilai dari nama, karena daftar tidak membawa uid
              // pemanggil. `claimer_username` diisi backend dari `sub` token, jadi
              // untuk klaim yang lahir dari web keduanya memang nama yang sama.
              const punyaSaya = !bebas && e.claimer_username === sesi?.nama
              const sedangAmbil = ambil.isPending && ambil.variables === e.id
              const sedangKirim = balas.isPending && balas.variables?.id === e.id
              return [
              <Baris key={e.id}>
                <td className="font-mono text-[11.5px] whitespace-nowrap">
                  {sejak(e.created_at, locale)}
                </td>
                <td className="font-mono text-[11.5px]">
                  {e.sender_username ? `@${e.sender_username}` : e.sender_id}
                </td>
                <td className="max-w-[360px]">
                  <span className="line-clamp-2">{e.original_text || '—'}</span>
                </td>
                <td>
                  <Chip nada="idle" titik={false}>
                    {e.type === 'comment' ? t('masuk.komentar') : t('masuk.dm')}
                  </Chip>
                </td>
                <td>
                  <Chip nada={e.bot_dipegang ? 'warn' : 'ok'}>
                    {e.bot_dipegang ? t('masuk.botDipegang') : t('masuk.botHidup')}
                  </Chip>
                </td>
                <td>
                  {bebas ? (
                    <Chip nada="bad">{t('masuk.belumDiambil')}</Chip>
                  ) : punyaSaya ? (
                    <Chip nada="accent">{t('masuk.andaAmbil')}</Chip>
                  ) : (
                    <Chip nada="accent">
                      {t('masuk.diambil', { nama: e.claimer_username || '—' })}
                    </Chip>
                  )}
                </td>
                <td className="text-right whitespace-nowrap">
                  {/* Yang dipegang orang lain tidak diberi tombol sama sekali —
                      tombol nonaktif hanya memancing klik lalu menjelaskan 409. */}
                  {bebas && (
                    <Tombol kecil onClick={() => ambil.mutate(e.id)} disabled={sedangAmbil}>
                      {sedangAmbil ? '…' : t('masuk.ambil')}
                    </Tombol>
                  )}
                  {punyaSaya && (
                    <Tombol
                      kecil
                      jenis="sekunder"
                      onClick={() => {
                        setMenulis(menulis === e.id ? null : e.id)
                        setTeks('')
                        balas.reset()
                      }}
                    >
                      {menulis === e.id ? t('umum.batal') : t('masuk.balas')}
                    </Tombol>
                  )}
                  {/* TIDAK PERLU DIBALAS — satu-satunya jalan keluar selain membalas.
                      Tanpa tombol ini spam, sapaan, dan orang yang sudah dijawab lewat
                      telepon duduk di antrean selamanya, dan antrean yang tidak pernah
                      habis berhenti dibaca orang.

                      Tidak menuntut diambil dulu, berbeda dari membalas: membalas
                      menulis atas nama seseorang, sedangkan ini keputusan tentang
                      antreannya. Yang dipegang ORANG LAIN tetap tidak diberi tombol —
                      ia sedang mengerjakannya. */}
                  {(bebas || punyaSaya) && (
                    <Tombol
                      kecil
                      jenis="sekunder"
                      disabled={abaikan.isPending}
                      onClick={() => abaikan.mutate(e.id)}
                    >
                      {t('masuk.abaikan')}
                    </Tombol>
                  )}
                </td>
              </Baris>,

              menulis === e.id ? (
                <tr key={`${e.id}-tulis`}>
                  <td colSpan={7} className="bg-surface2 px-3.5 py-3">
                    <div className="flex flex-col gap-2 max-w-[70ch]">
                      <p className="m-0 text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                        {t('masuk.balasKe', {
                          nama: e.sender_username ? `@${e.sender_username}` : e.sender_id,
                        })}
                      </p>
                      <textarea
                        autoFocus
                        rows={3}
                        value={teks}
                        maxLength={2000}
                        onChange={(ev) => setTeks(ev.target.value)}
                        placeholder={t('masuk.tulisBalasan')}
                        className="w-full text-[13px] text-ink bg-surface border border-lineStrong
                                   rounded-[6px] px-3 py-2 resize-y
                                   transition-colors hover:border-ink3 focus:border-ink"
                      />
                      {balas.isError && (
                        <Bendera nada="bad" role="alert">{balas.error?.message}</Bendera>
                      )}
                      <div className="flex items-center gap-2">
                        <Tombol
                          kecil
                          disabled={!teks.trim() || sedangKirim}
                          onClick={() =>
                            balas.mutate(
                              { id: e.id, teks: teks.trim() },
                              { onSuccess: () => { setMenulis(null); setTeks('') } },
                            )
                          }
                        >
                          {sedangKirim ? t('masuk.mengirim') : t('masuk.kirim')}
                        </Tombol>
                        <span className="text-[11.5px] text-ink3">
                          {t('masuk.langsungTerkirim')}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : null,
              ]
            })}
          </Tabel>
        )}
      </Panel>
    </>
  )
}
